/**
 * Learn-Mode Inline Tips — registry.
 *
 * Flat lookup from `data-help-context` key → tip, assembled from every area
 * module below. Add a new area's tips here as coverage grows.
 */

import { accountsTips } from "./accounts";
import { analyticsTips } from "./analytics";
import { approvalsTips } from "./approvals";
import { archiveTips } from "./archive";
import { autopilotTips } from "./autopilot";
import { brandTips } from "./brand";
import { calendarTips } from "./calendar";
import { calendarEventsTips } from "./calendar-events";
import { commandPaletteTips } from "./command-palette";
import { composerTips } from "./composer";
import { contentIdeasTips } from "./content-ideas";
import { contentLibraryTips } from "./content-library";
import { designEditorTips } from "./design-editor";
import { designStudioTips } from "./design-studio";
import { engagementTips } from "./engagement";
import { footageLibraryTips } from "./footage-library";
import { hashtagsTips } from "./hashtags";
import { inboxTips } from "./inbox";
import { linkInBioTips } from "./link-in-bio";
import { listeningTips } from "./listening";
import { paidMediaTips } from "./paid-media";
import { queueTips } from "./queue";
import { reportsTips } from "./reports";
import { seriesTips } from "./series";
import { settingsTips } from "./settings";
import { templatesTips } from "./templates";
import type { LearnTip, LearnTipsByArea } from "./types";
import { videoEditorTips } from "./video-editor";

export type { LearnTip, LearnTipsByArea } from "./types";

/** Map of area → inline tips for that area. Area names are organizational
 * only (unlike the portal's page-key routing) — every tip is looked up by its
 * own `key`, not by area, since the Social SPA's help overlay scans the live
 * DOM for `[data-help-context]` rather than a per-route page key. */
export const TIPS_BY_AREA: LearnTipsByArea = {
  accounts: accountsTips,
  analytics: analyticsTips,
  approvals: approvalsTips,
  archive: archiveTips,
  autopilot: autopilotTips,
  brand: brandTips,
  calendar: calendarTips,
  "calendar-events": calendarEventsTips,
  "command-palette": commandPaletteTips,
  composer: composerTips,
  "content-ideas": contentIdeasTips,
  "content-library": contentLibraryTips,
  "design-editor": designEditorTips,
  "design-studio": designStudioTips,
  engagement: engagementTips,
  "footage-library": footageLibraryTips,
  hashtags: hashtagsTips,
  inbox: inboxTips,
  "link-in-bio": linkInBioTips,
  listening: listeningTips,
  "paid-media": paidMediaTips,
  queue: queueTips,
  reports: reportsTips,
  series: seriesTips,
  settings: settingsTips,
  templates: templatesTips,
  "video-editor": videoEditorTips,
};

/**
 * Flat map of context_key → LearnTip across every area. Used by
 * `LearnTipsOverlay` to resolve a `data-help-context` element's content
 * without knowing which area it belongs to.
 */
export const TIPS_BY_CONTEXT_KEY: Record<string, LearnTip> = Object.values(TIPS_BY_AREA)
  .flat()
  .reduce<Record<string, LearnTip>>((acc, tip) => {
    acc[tip.key] = tip;
    return acc;
  }, {});

/** Look up a single inline tip by its context_key (null when not found). */
export function getTipByContextKey(contextKey: string): LearnTip | null {
  return TIPS_BY_CONTEXT_KEY[contextKey] ?? null;
}

/** Every tip across every area, flattened (used by the coverage test). */
export function getAllTips(): LearnTip[] {
  return Object.values(TIPS_BY_AREA).flat();
}
